import { useControlledState } from '@react-stately/utils';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import * as React from 'react';

import {
    AutoComplete,
    type AutoCompleteProps,
    Badge,
    Heading,
    Menu,
    MenuItem,
    MenuPlaceholder,
    MenuRoot,
    MenuSearch,
    MenuSection,
    type Placement,
    Popover,
    Separator,
    SubMenu,
} from '@gitbook/spine-react';
import { usePersistentValue } from '@gitbook/util-react-hooks';

import { typeQueryInPalette } from '../transforms';
import {
    type ComputedCommandsSection,
    type PaletteCommandDef,
    PaletteCommandType,
    type PaletteDef,
    type PaletteGroupDef,
    type VisiblePalette,
} from '../types';
import { computeCommandsGroup } from './useComputePaletteCommands';

/**
 * Menu root that accepts a palette.
 */
export function PaletteMenuRoot(props: {
    visiblePalette: VisiblePalette | null;
    onVisiblePaletteChange: (visiblePalette: VisiblePalette | null) => void;
}) {
    const { onVisiblePaletteChange } = props;
    const isOpen = Boolean(props.visiblePalette);
    const visiblePalette = usePersistentValue(props.visiblePalette);
    if (!visiblePalette) {
        return null;
    }
    const { palette } = visiblePalette;
    return (
        <MenuRoot
            isOpen={isOpen}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    onVisiblePaletteChange(null);
                }
            }}
            triggerRef={visiblePalette.anchorRef}
        >
            <Popover
                restoreFocus={!visiblePalette.inputRef}
                placement={getPlacementFromPaletteDef(palette)}
            >
                <PaletteAutoCompleteMenu
                    palette={palette}
                    onPaletteChange={(palette) => {
                        onVisiblePaletteChange({
                            ...visiblePalette,
                            palette,
                        });
                    }}
                    inputRef={visiblePalette.inputRef}
                />
            </Popover>
        </MenuRoot>
    );
}

function Boundaries(props: { children: React.ReactNode }) {
    return (
        <Sentry.ErrorBoundary
            fallback={<MenuPlaceholder>Error while loading actions</MenuPlaceholder>}
        >
            <React.Suspense
                fallback={
                    <Menu>
                        <MenuPlaceholder>Loading…</MenuPlaceholder>
                    </Menu>
                }
            >
                {props.children}
            </React.Suspense>
        </Sentry.ErrorBoundary>
    );
}

/**
 * We don't use a component to avoid breaking the reading of the tree.
 */
function wrapAutoComplete(
    props: {
        search: PaletteDef['search'] | undefined;
        queryText: string;
        onQueryTextChange?: (query: string) => void;
        isPending?: boolean;
    } & Pick<AutoCompleteProps, 'inputRef' | 'children' | 'filterValue'>
) {
    const { queryText, search, inputRef, onQueryTextChange, isPending, filterValue, children } =
        props;

    if (queryText || search || inputRef) {
        return (
            <AutoComplete
                defaultValue={search?.initialValue}
                filterValue={filterValue}
                value={queryText}
                onValueChange={onQueryTextChange}
                inputRef={inputRef}
            >
                {search && <MenuSearch isPending={isPending} label={search?.placeholder} />}
                {children}
            </AutoComplete>
        );
    }

    return children;
}

type PaletteChangeCallback = (palette: PaletteDef) => void;

class PaletteMenuCache {
    #cache: Map<string, Map<PaletteGroupDef, ComputedCommandsSection | null>> = new Map();
    get(props: {
        palette: PaletteDef;
        group: PaletteGroupDef;
    }): ComputedCommandsSection | null | undefined {
        const { palette, group } = props;
        const groupsCache = this.#cache.get(palette.query.text);
        if (!groupsCache) {
            return undefined;
        }
        return groupsCache.get(group);
    }
    set(
        props: { palette: PaletteDef; group: PaletteGroupDef },
        value: ComputedCommandsSection | null
    ): void {
        const { palette, group } = props;
        let groupsCache = this.#cache.get(palette.query.text);
        if (!groupsCache) {
            groupsCache = new Map();
            this.#cache.set(palette.query.text, groupsCache);
        }
        groupsCache.set(group, value);
    }
}

const PaletteAutoCompleteMenu = React.memo(function PaletteAutoCompleteMenuMemo(
    props: {
        defaultPalette?: PaletteDef;
        palette?: PaletteDef;
        onPaletteChange?: (palette: PaletteDef) => void;
        cache?: PaletteMenuCache;
    } & Pick<AutoCompleteProps, 'inputRef'>
) {
    const {
        palette: paletteProp,
        onPaletteChange,
        defaultPalette: defaultPaletteProp,
        cache: cacheProp,
        inputRef,
    } = props;
    const defaultPalette = defaultPaletteProp ?? paletteProp;
    if (!defaultPalette) {
        throw new Error(`Either "defaultPalette" or "palette" should be filled`);
    }
    const [palette, setPalette] = useControlledState(paletteProp, defaultPalette, onPaletteChange);
    const deferredPalette = React.useDeferredValue(palette);
    const [cache] = React.useState<PaletteMenuCache>(() => cacheProp ?? new PaletteMenuCache());
    return wrapAutoComplete({
        isPending: deferredPalette !== palette,
        search: deferredPalette.search,
        queryText: palette.query.text,
        onQueryTextChange: (query) => setPalette(typeQueryInPalette(palette, query)),
        inputRef,
        filterValue: defaultPalette.query.text,
        children: (
            <Boundaries>
                <PaletteMenu palette={deferredPalette} onPaletteChange={setPalette} cache={cache} />
            </Boundaries>
        ),
    });
});

function PaletteMenu(props: {
    palette: PaletteDef;
    onPaletteChange: (palette: PaletteDef) => void;
    cache: PaletteMenuCache;
}) {
    return <Menu>{getSectionsFromPalette(props)}</Menu>;
}

function getPlacementFromPaletteDef(def: PaletteDef) {
    const alignment =
        def.position?.preferredAlignment === 'end' || def.position?.preferredAlignment === 'start'
            ? def.position.preferredAlignment
            : null;
    const placement =
        `${def.position?.preferredPlacement ?? 'bottom'}${alignment ? `-${alignment}` : ''}` as Placement;
    return placement;
}

function getSectionsFromPalette(props: {
    palette: PaletteDef;
    onPaletteChange: PaletteChangeCallback;
    cache: PaletteMenuCache;
}) {
    const { palette, onPaletteChange, cache } = props;
    const sections = palette.groups.flatMap((group) => {
        let commandsSection = cache.get({ palette, group });
        if (commandsSection === undefined) {
            const computed = computeCommandsGroup(palette, group);

            if (computed instanceof Promise) {
                throw computed;
            }

            commandsSection = computed ?? null;
            cache.set({ palette, group }, commandsSection);
        }

        if (!commandsSection) {
            return [];
        }

        const items = commandsSection.data.map((command) => {
            const item = getMenuItemFromPaletteCommandDef({
                command,
                onPaletteChange,
            });
            if (command.descendant) {
                const subPalette = command.descendant();
                return (
                    <SubMenu key={`${command.key}-submenu`}>
                        {item}
                        <Popover>
                            {subPalette.static ? (
                                wrapAutoComplete({
                                    search: subPalette.search,
                                    queryText: subPalette.query.text,
                                    children: (
                                        <Menu>
                                            {getSectionsFromPalette({
                                                palette: subPalette,
                                                onPaletteChange: () => {
                                                    throw new Error(
                                                        `Trigger that returns a new palette is unsupported with static palette option`
                                                    );
                                                },
                                                cache,
                                            })}
                                        </Menu>
                                    ),
                                })
                            ) : (
                                <PaletteAutoCompleteMenu
                                    defaultPalette={subPalette}
                                    cache={cache}
                                />
                            )}
                        </Popover>
                    </SubMenu>
                );
            }
            return item;
        });

        if (items.length === 0) {
            return [];
        }

        const separator = <Separator key={`${group.key}-separator`} />;

        if (commandsSection?.group.title) {
            return [
                <MenuSection key={group.key}>
                    <Heading>{group.title}</Heading>
                    {items}
                </MenuSection>,
                separator,
            ];
        }
        return [...items, separator];
    });
    if (sections.length > 0 && sections[sections.length - 1].type === Separator) {
        sections.pop();
    }
    return sections;
}

function getMenuItemFromPaletteCommandDef(args: {
    command: PaletteCommandDef;
    onPaletteChange: PaletteChangeCallback;
}) {
    const { command, onPaletteChange } = args;
    const { trigger } = command;
    return (
        <MenuItem
            key={command.key}
            icon={command.icon}
            checked={command.selected ?? command.enabled}
            className={clsx(command.upgrade && '[--menu-item-focused-bg:--sp-color-bg-upgrade]')}
            checkbox={command.type === PaletteCommandType.CHECKBOX}
            suffix={
                command.badge ? (
                    <Badge className="shrink-0" size="xsmall" {...command.badge} />
                ) : null
            }
            disabled={command.disabled}
            subtitle={
                command.subtitle ? (
                    typeof command.subtitle === 'string' ? (
                        command.subtitle
                    ) : (
                        <LazySubtitle subtitle={command.subtitle} />
                    )
                ) : undefined
            }
            keywords={command.keywords}
            keyboardShortcut={'keyboardShortcut' in command ? command.keyboardShortcut : undefined}
            onAction={
                trigger
                    ? (event) => {
                          const triggerResult = trigger();
                          if (triggerResult instanceof Promise) {
                              return triggerResult.then((palette) => {
                                  if (palette) {
                                      event.preventDefault();
                                      onPaletteChange(palette);
                                  }
                              });
                          } else if (triggerResult) {
                              event.preventDefault();
                              onPaletteChange(triggerResult);
                          }
                      }
                    : undefined
            }
        >
            {command.title}
        </MenuItem>
    );
}

function LazySubtitle(props: { subtitle: () => string }) {
    return props.subtitle();
}
